import { supabase } from '@/lib/supabase';
import { computeZmanimDates } from '@/lib/hebcal';
import { fetchUserProfile, profileGeo } from '@/services/profile';

interface UpcomingReminder {
  id: string;
  habitName: string;
  icon: string;
  color: string;
  scheduledTime: string;
  timeLabel: string;
  provider: string;
  providerIcon: string;
  sortMinutes: number;
}

const providerIcons: Record<string, string> = {
  phone: 'ri-phone-line',
  sms: 'ri-message-line',
  email: 'ri-mail-line',
  whatsapp: 'ri-whatsapp-line',
  push: 'ri-notification-3-line',
  telegram: 'ri-telegram-line',
};

interface UserHabitRow {
  id: string;
  enabled: boolean;
  notification_providers: string[] | null;
  custom_settings: Record<string, unknown> | null;
  habit_types: {
    name: string;
    schedule_type: string;
    calendar_icon: string | null;
    custom_settings: Record<string, unknown> | null;
  } | null;
}

function formatFixedTime(hhmm: string): { display: string; minutes: number } {
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return { display: '--:--', minutes: Infinity };
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { display: `${h12}:${String(m).padStart(2, '0')} ${period}`, minutes: h * 60 + m };
}

function parseOffsetMinutes(offset: unknown): number {
  if (typeof offset !== 'string') return 0;
  const match = offset.match(/^([+-]?)(\d+)m?$/);
  if (!match) return 0;
  return (match[1] === '-' ? -1 : 1) * Number(match[2]);
}

export default function UpcomingRemindersWidget() {
  const [reminders, setReminders] = useState<UpcomingReminder[] | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [profile, { data }] = await Promise.all([
        fetchUserProfile(),
        supabase
          .from('user_habits')
          .select('id, enabled, notification_providers, custom_settings, habit_types(name, schedule_type, calendar_icon, custom_settings)')
          .eq('enabled', true),
      ]);
      if (!mounted) return;
      const geo = profileGeo(profile);
      const zmanDates = computeZmanimDates(new Date(), geo);
      const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: geo.timezone });

      const rows = (data || []) as unknown as UserHabitRow[];
      const list: UpcomingReminder[] = rows.map((row) => {
        const ht = row.habit_types;
        const cs = row.custom_settings || {};
        const htCs = ht?.custom_settings || {};
        const scheduleType = ht?.schedule_type || 'fixed_time';
        let scheduledTime = '--:--';
        let sortMinutes = Infinity;
        let timeLabel = '';

        if (scheduleType === 'fixed_time') {
          const hhmm = (cs.fixed_time as string) || (htCs.ideal_time as string) || '';
          if (hhmm) {
            const f = formatFixedTime(hhmm);
            scheduledTime = f.display;
            sortMinutes = f.minutes;
          }
        } else if (scheduleType === 'zman_based') {
          const zmanRef = (cs.zman_reference as string) || (htCs.zman_reference as string) || '';
          const base = zmanDates[zmanRef];
          if (base && !isNaN(base.getTime())) {
            const offsetMin = parseOffsetMinutes(cs.offset ?? htCs.offset);
            const target = new Date(base.getTime() + offsetMin * 60000);
            scheduledTime = timeFmt.format(target);
            sortMinutes = target.getHours() * 60 + target.getMinutes();
            timeLabel = '~via zman';
          }
        } else {
          timeLabel = scheduleType === 'manual' ? 'manual' : 'relative';
        }

        const provider = row.notification_providers?.[0] || 'phone';
        return {
          id: row.id,
          habitName: ht?.name || 'Habit',
          icon: ht?.calendar_icon || 'ri-notification-4-line',
          color: 'text-primary-600',
          scheduledTime,
          timeLabel,
          provider,
          providerIcon: providerIcons[provider] || 'ri-phone-line',
          sortMinutes,
        };
      });

      list.sort((a, b) => a.sortMinutes - b.sortMinutes);
      setReminders(list.slice(0, 4));
    })();
    return () => { mounted = false; };
  }, []);

  if (!reminders) {
    return (
      <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden animate-pulse">
        <div className="px-4 py-3 border-b border-background-200/60">
          <div className="h-5 w-40 rounded bg-background-200" />
        </div>
        <div className="p-4"><div className="h-40 rounded bg-background-200/50" /></div>
      </div>
    );
  }

  return (
    <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-notification-4-line text-primary-500 text-lg" />
          </span>
          <h3 className="text-sm font-heading font-semibold text-foreground-950">Upcoming Reminders</h3>
        </div>
        <span className="text-[10px] text-foreground-400 font-mono bg-background-200 px-2 py-0.5 rounded-full">
          Today
        </span>
      </div>

      <div className="p-2">
        {reminders.length === 0 ? (
          <p className="text-xs text-foreground-400 text-center py-4">No reminders scheduled</p>
        ) : (
          <div className="space-y-1">
            {reminders.map((reminder, idx) => (
              <div
                key={reminder.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-background-200 transition cursor-default group"
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${idx === 0 ? 'bg-primary-50' : 'bg-background-200'}`}>
                  <i className={`${reminder.icon} ${reminder.color} text-sm`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-900 truncate">{reminder.habitName}</p>
                  <div className="flex items-center gap-2">
                    <span className={`w-3.5 h-3.5 flex items-center justify-center ${idx === 0 ? 'text-primary-500' : 'text-foreground-400'}`}>
                      <i className={`${reminder.providerIcon} text-[10px]`} />
                    </span>
                    {reminder.timeLabel && (
                      <span className="text-[10px] text-foreground-400">{reminder.timeLabel}</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`text-sm font-mono font-semibold ${idx === 0 ? 'text-primary-600' : 'text-foreground-700'}`}>
                    {reminder.scheduledTime}
                  </span>
                  {idx === 0 && (
                    <div className="mt-0.5">
                      <span className="text-[10px] text-primary-500 bg-primary-50 px-1.5 py-0.5 rounded-full font-medium">
                        next
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}